import { useEffect, useMemo, useState } from 'react'
import Layout from '@Components/Layout'
import { useAppDispatch, useAppSelector } from '@Hooks/ReduxHooks'
import { bootstrapAuthAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { getAssets, getEducationalInfo, getSettings, updateOnlineStatus } from '@Features/Settings/SettingsSlice/Settings.slice'
import { dashboardAPI, useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { RoutingProvider } from './Providers/RoutingProvider/RoutingProvider'
import { SnackbarProvider } from '@eduact/ed-components'
import ApiProvider from '@Providers/ApiProvider/ApiProvider'
import EdSnackbarProvider from '@Providers/EdSnackbarProvider/EdSnackbarProvider'
import { checkRunningTest } from '@Redux/RunningTestSlice/RunningTestSlice'
import { useSearchParams } from 'react-router-dom'
import { ThemeProvider } from '@eduact/student-theme'
import { Reset } from 'styled-reset'
import GlobalStyles from '@Styles/GlobalStyles'
import QuillContentStyles from '@Styles/QuillContentStyles'
import 'katex/dist/katex.min.css'
import { useDeviceInfo } from '@eduact/utils'
import LocalStorageKeys from '@Constants/LocalStorageKeys'

import { ThemeSelector } from '@Providers/ThemeSelector/ThemeSelector'
import { useBrowserTabInit } from '@Hooks/useBrowserTabInit'
import { useMarketingTags } from '@Hooks/useMarketingTags'
import WaselChat from '@Components/WaselChat/WaselChat'
import { setEmbed } from '@Redux/UtilsSlice/UtilsSlice'

/** Hidden longer than this → the session may have died meanwhile; re-ask. */
const REVALIDATE_AFTER_HIDDEN_MS = 5 * 60 * 1000

export default function App() {
	const dispatch = useAppDispatch()
	const { isAuth, authReady } = useAppSelector(state => state.auth)
	// No detectLocation: it fired navigator.geolocation.getCurrentPosition() on
	// the first paint of EVERY route, so a visitor got a browser permission
	// prompt before seeing anything. Both auth validators had the field optional
	// and the server discarded it.
	useDeviceInfo({
		keyName: LocalStorageKeys.DeviceId,
	})

	useGetUserProfileQuery(undefined, {
		skip: !isAuth,
	})

	// Repair the profile entry once the server has answered.
	//
	// getUserProfile's queryFn reads `isAuth` from Redux and, when false, returns
	// null WITHOUT issuing a request — and RTK Query caches that null like any
	// other success. The skip above protects this call site, but LearningTimeWidget
	// subscribes with no skip and shares the cache key, so on a reload it runs the
	// queryFn while auth is still unresolved and poisons the shared entry. Nothing
	// invalidated 'Profile' afterwards, so the dashboard rendered with an empty
	// profile — looking signed out — for the life of the page.
	//
	// Invalidating is the same remedy VerifyDevice and ClassroomSlice already use,
	// and it repairs the entry whichever call site poisoned it. Fires at most once
	// per sign-in: the quiet revalidation below flips neither flag.
	useEffect(() => {
		if (authReady && isAuth) dispatch(dashboardAPI.util.invalidateTags(['Profile']))
	}, [authReady, isAuth])

	useBrowserTabInit({ auto: true })

	// GTM + all vendor pixels, injected once getSettings delivers the IDs the
	// admin entered on Settings → Marketing. Replaces the old hardcoded
	// FacebookPixel/ReactGA init that fired the legacy pixel on every brand.
	useMarketingTags()

	useEffect(() => {
		// Ask the server whether this session is live. The credential is an
		// httpOnly cookie now, so there is nothing readable here to infer it from
		// — checkAuthAction's localStorage read could only ever have been a guess.
		//
		// Skipped during an impersonation handoff. Both this and the redemption
		// resolve auth state and both reducers overwrite user/isAuth, so a
		// bootstrap that happens to land last — while the cookie is still being
		// set — would blank a successful redemption.
		if (!params.get('impersonationCode')) dispatch(bootstrapAuthAction())
		dispatch(getSettings())
		dispatch(getAssets())
		dispatch(getEducationalInfo())
		dispatch(checkRunningTest())
		dispatch(setEmbed(!!Boolean(params.get('embed'))))
		window.addEventListener('online', e => handleOnlineChange(true))
		window.addEventListener('offline', e => handleOnlineChange(false))
		return () => {
			window.removeEventListener('online', e => handleOnlineChange(true))
			window.removeEventListener('offline', e => handleOnlineChange(false))
		}
	}, [])

	// The boot answer above can go stale in a living tab: the session dies
	// server-side while the tab sits open, or a bfcache Back restores a
	// pre-death heap without re-running this mount. The public/logged-in hybrid
	// pages answer a dead cookie with guest-shaped 200s — never a 401 — so
	// nothing else ever corrects `isAuth`. Re-ask at the two staleness points.
	// `quiet` keeps authReady true so guarded subtrees don't flash-unmount.
	useEffect(() => {
		const revalidate = () => dispatch(bootstrapAuthAction({ quiet: true }))
		const handlePageShow = (e: PageTransitionEvent) => {
			if (e.persisted) revalidate()
		}
		let hiddenAt: number | null = null
		const handleVisibility = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAt = Date.now()
			} else if (hiddenAt !== null && Date.now() - hiddenAt >= REVALIDATE_AFTER_HIDDEN_MS) {
				hiddenAt = null
				revalidate()
			}
		}
		window.addEventListener('pageshow', handlePageShow)
		document.addEventListener('visibilitychange', handleVisibility)
		return () => {
			window.removeEventListener('pageshow', handlePageShow)
			document.removeEventListener('visibilitychange', handleVisibility)
		}
	}, [])

	const handleOnlineChange = (online: boolean) => {
		dispatch(updateOnlineStatus(online))
	}
	const [params] = useSearchParams()

	const isEmbed = useMemo(() => {
		return Boolean(params.get('embed'))
	}, [])

	return (
		<ThemeSelector>
			{(theme, setTheme) => {
				return (
					<>
						<ThemeProvider theme={theme} setTheme={setTheme}>
							<GlobalStyles />
							<QuillContentStyles />
							<Reset />
							<RoutingProvider>
								<SnackbarProvider>
									<EdSnackbarProvider>
										<ApiProvider>
											<Layout embed={isEmbed} />
											<WaselChat />
										</ApiProvider>
									</EdSnackbarProvider>
								</SnackbarProvider>
							</RoutingProvider>
						</ThemeProvider>
					</>
				)
			}}
		</ThemeSelector>
	)
}
